import {
  ScatterChart,
  Scatter,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useMemo } from 'react';
import { getMetricAxisFormatter, shortToolId } from '../../utils/format';
import { Link } from 'react-router-dom';
// Only one valid return statement below
import { formatXValue, getMetricFormatter, getMetricUnit } from '@/utils/format';
import {
  aggregateTracesByTool,
  aggregateTracesByCategory,
  aggregateTracesByReference,
} from '../../utils/trace';
import { getColorForCategory } from '../../utils/palette';
import { getCategoryValue, getMetricValue, getXMetricValue, isCategoricalXAxis } from '../../utils/helper';
import { computeYFromTraces } from '../../utils/aggregationUtils';
import { SmartLegend } from './SmartLegend';
import type { LegendItem } from './SmartLegend';
import { ChartEmptyState } from './ChartEmptyState';
import { useTranslation } from '@/hooks/useTranslation';
import { getSafeAggregationLevel } from '../../utils/chartSafety';
import { getMetricLabelKey } from '../../utils/chartDefaults';

import type { ChartConfig } from '../../type/dynamicChart';
import type { Trace } from '@/features/traceability/type/Tool';
type TLScatterChartProps = {
  config: ChartConfig;
  traces: Trace[];
};

// Helper to get color for a category
// Check if X-axis is a categorical dimension
export function TLScatterChart({ config, traces }: TLScatterChartProps) {
  const { t } = useTranslation();

  // Check if X-axis is categorical dimension
  const isCategorical = isCategoricalXAxis(config.xAxis.metric);

  // For categorical X-axis, always aggregate by category
  // For toolId X-axis, respect aggregationLevel setting with safety check
  const aggregationLevel = getSafeAggregationLevel(
    isCategorical ? 'category' : config.aggregatedBy,
    traces,
    isCategorical
  );

  const aggregatedData = useMemo(() => {
    if (isCategorical) {
      // Aggregate traces by categorical dimension
      return aggregateTracesByCategory(
        traces,
        config.xAxis.metric as 'machine' | 'material' | 'toolHolder' | 'lubrication'
      );
    }
    if (aggregationLevel === 'reference') {
      return aggregateTracesByReference(traces);
    }
    if (aggregationLevel === 'trace') {
      return traces;
    }
    // Aggregate by tool
    return aggregateTracesByTool(traces);
  }, [traces, aggregationLevel, isCategorical, config.xAxis.metric]);

  // Transform data into chart data
  const chartData = aggregatedData
    .map((data, index) => {
      // Handle CategoryAggregation differently
      if ('category' in data) {
        // CategoryAggregation from categorical X-axis — use computeYFromTraces with rawTraces
        const yValue = computeYFromTraces(data.rawTraces, config.yAxis.metric, config.yAxis.aggregation);
        return {
          x: data.category, // Use category name as X value
          y: yValue,
          toolId: data.category, // Display category name instead of toolId
          fullToolId: data.category,
          traceId: undefined,
          fullTraceId: undefined,
          category: data.category, // Color by category itself
          index,
          traceCount: data.traceCount,
        };
      }
      // Handle ReferenceAggregation — keep existing getMetricValue (not in F2 scope)
      if ('reference' in data && 'toolCount' in data) {
        const yValue = getMetricValue(data, config.yAxis.metric);
        if (yValue === null) return null;
        return {
          x: data.reference, // Use reference as X value
          y: yValue,
          toolId: data.reference, // Use reference as toolId for display
          fullToolId: data.reference,
          traceId: undefined,
          fullTraceId: undefined,
          category: data.reference, // Color by reference
          index,
          traceCount: data.traceCount,
          toolCount: data.toolCount,
        };
      }
      // Handle ToolAggregation (has rawTraces) vs raw Trace
      if ('rawTraces' in data) {
        // ToolAggregation — use computeYFromTraces with rawTraces
        const yValue = computeYFromTraces(data.rawTraces, config.yAxis.metric, config.yAxis.aggregation);
        const xValue =
          aggregationLevel === 'trace' ? index : getXMetricValue(data, config.xAxis.metric);
        if (xValue === null) return null;
        return {
          x: xValue,
          y: yValue,
          toolId: shortToolId(data.toolId),
          fullToolId: data.toolId,
          traceId: undefined,
          fullTraceId: undefined,
          category:
            aggregationLevel === 'trace'
              ? shortToolId(data.toolId)
              : getCategoryValue(data, config.options.colorBy),
          index,
          traceCount: data.traceCount,
        };
      }
      // Handle raw Trace (single trace — no aggregation needed)
      const yValue = getMetricValue(data, config.yAxis.metric);
      if (yValue === null) return null;
      // For trace level: use index as x, for tool level: use metric value
      const xValue =
        aggregationLevel === 'trace' ? index : getXMetricValue(data, config.xAxis.metric);
      if (xValue === null) return null;
      return {
        x: xValue,
        y: yValue,
        toolId: shortToolId(data.toolId),
        fullToolId: data.toolId,
        traceId: 'traceId' in data ? data.traceId : undefined,
        fullTraceId: 'traceId' in data ? data.traceId : undefined,
        // For trace level, color by tool; otherwise use config
        category:
          aggregationLevel === 'trace'
            ? shortToolId(data.toolId)
            : getCategoryValue(data, config.options.colorBy),
        index,
        // Add trace count for aggregated data
        traceCount: 'traceCount' in data ? data.traceCount : 1,
      };
    })
    .filter((d): d is NonNullable<typeof d> => d !== null);

  // Get unique categories for color mapping
  // For trace level, always color by tool; otherwise use config
  const categories = Array.from(new Set(chartData.map((d) => d.category)));

  // Prepare legend items for SmartLegend
  const legendItems: LegendItem[] = useMemo(() => {
    return categories.map((cat) => ({
      value: cat,
      label: cat,
      color: getColorForCategory(cat, categories),
    }));
  }, [categories]);

  if (chartData.length === 0) {
    return <ChartEmptyState variant="no-data" />;
  }

  // A single <Scatter> with per-point <Cell> colours: with one <Scatter> per category
  // (each holding its own data) the Tooltip payload mixes in unrelated items and
  // payload[0] is not the hovered point (recharts#6075, Recharts 3.1.x) — every
  // tooltip then showed the same wrong value.

  // For categorical X-axis, use category type
  // For trace level, x is sequential index (category type)
  // For tool level, x is numeric metric value
  const isXNumeric =
    !isCategorical &&
    aggregationLevel === 'tool' &&
    config.xAxis.metric !== 'traceId' &&
    config.xAxis.metric !== 'toolId';

  const xFormatter = isCategorical
    ? (value: number | string) => String(value) // Show category name as-is
    : aggregationLevel === 'trace'
      ? (value: number | string) => `Trace ${Number(value) + 1}`
      : formatXValue;

  const yFormatter = getMetricFormatter(config.yAxis.metric);

  const xAxisFormatter = isCategorical
    ? (value: number | string) => String(value) // Show category name as-is
    : aggregationLevel === 'trace'
      ? (value: number | string) => String(Number(value) + 1)
      : formatXValue;

  const yAxisFormatter = getMetricAxisFormatter(config.yAxis.metric);
  const yUnit = getMetricUnit(config.yAxis.metric);

  return (
    <div className="space-y-2">
      <ResponsiveContainer width="100%" height={450}>
        <ScatterChart margin={{ top: 8, right: 16, bottom: 60, left: 52 }}>
          {config.options.showGridLines !== false && <CartesianGrid strokeDasharray="3 3" />}
          <XAxis
            type={isXNumeric ? 'number' : 'category'}
            dataKey="x"
            name={aggregationLevel === 'trace' ? t('analytics.dashboard.complexCharts.axis.traceIndex') : t(getMetricLabelKey(config.xAxis.metric))}
            allowDuplicatedCategory={false}
            angle={-45}
            textAnchor="end"
            height={52}
            tickFormatter={xAxisFormatter}
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            label={{
              value: aggregationLevel === 'trace' ? t('analytics.dashboard.complexCharts.axis.traceIndex') : t(getMetricLabelKey(config.xAxis.metric)),
              position: 'insideBottom',
              offset: -40,
              fill: 'var(--muted-foreground)',
            }}
          />
          <YAxis
            type="number"
            dataKey="y"
            name={t(getMetricLabelKey(config.yAxis.metric))}
            tickFormatter={yAxisFormatter}
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            label={{
              value: `${t(getMetricLabelKey(config.yAxis.metric))} ${yUnit ? `(${yUnit})` : ''}`,
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
              fill: 'var(--muted-foreground)',
            }}
          />
          <Tooltip
            cursor={{ strokeDasharray: '3 3' }}
            content={({ payload }) => {
              if (!payload || payload.length === 0) return null;
              const data = payload[0].payload;
              // Reference aggregation tooltip
              if ('toolCount' in data && 'reference' in data) {
                return (
                  <div className="bg-popover border border-border shadow-md rounded-lg p-3 text-xs">
                    <p className="font-medium">
                      {t('analytics.dashboard.complexCharts.tooltip.reference')}: {data.reference}
                    </p>
                    <p className="font-mono">
                      {t('analytics.dashboard.complexCharts.tooltip.toolCount', {
                        count: data.toolCount,
                      })}
                    </p>
                    <p className="font-mono">
                      {t('analytics.dashboard.complexCharts.tooltip.aggregatedFromTools', {
                        count: data.toolCount,
                      })}
                    </p>
                    <p className="font-mono">
                      {t(getMetricLabelKey(config.yAxis.metric))}: {yFormatter(data.y)}
                    </p>
                    <p className="font-mono">
                      {t('analytics.dashboard.complexCharts.tooltip.traceCount', {
                        count: data.traceCount,
                      })}
                    </p>
                  </div>
                );
              }
              // Categorical X-axis: show category name as main identifier
              if (isCategorical) {
                return (
                  <div className="bg-popover border border-border shadow-md rounded-lg p-3 text-xs">
                    <p className="font-medium">
                      {t(getMetricLabelKey(config.xAxis.metric))}: {data.toolId}
                    </p>
                    <p className="font-mono">
                      {t(getMetricLabelKey(config.yAxis.metric))}: {yFormatter(data.y)}
                    </p>
                    {data.traceCount > 1 && (
                      <p className="font-mono text-muted-foreground">
                        {t('analytics.dashboard.complexCharts.tooltip.aggregatedFrom', {
                          count: data.traceCount,
                        })}
                      </p>
                    )}
                  </div>
                );
              }
              // Normal mode: show toolId as main identifier
              return (
                <div className="bg-popover border border-border shadow-md rounded-lg p-3 text-xs">
                  <p className="font-medium">
                    {t('analytics.dashboard.complexCharts.tooltip.tool')}:{' '}
                    <Link
                      to={`/traceability/tools/${encodeURIComponent(data.fullToolId)}`}
                      className="text-primary hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {data.fullToolId}
                    </Link>
                  </p>
                  {aggregationLevel === 'trace' && data.traceId && (
                    <p className="font-mono">
                      {t('analytics.dashboard.complexCharts.tooltip.trace')}: {data.traceId}
                    </p>
                  )}
                  {aggregationLevel === 'trace' ? (
                    <p className="font-mono">
                      {t('analytics.dashboard.complexCharts.tooltip.traceIndex')}:{' '}
                      {typeof data.x === 'number' ? data.x + 1 : data.x}
                    </p>
                  ) : (
                    <>
                      {config.options.colorBy && (
                        <p className="font-mono">
                          {t(`analytics.dashboard.complexCharts.configuration.metrics.${config.options.colorBy}`)}: {data.category}
                        </p>
                      )}
                      <p className="font-mono">
                        {t(getMetricLabelKey(config.xAxis.metric))}: {xFormatter(data.x)}
                      </p>
                    </>
                  )}
                  <p className="font-mono">
                    {t(getMetricLabelKey(config.yAxis.metric))}: {yFormatter(data.y)}
                  </p>
                  {data.traceCount > 1 && (
                    <p className="font-mono text-muted-foreground">
                      {t('analytics.dashboard.complexCharts.tooltip.aggregatedFrom', {
                        count: data.traceCount,
                      })}
                    </p>
                  )}
                </div>
              );
            }}
          />
          <Scatter data={chartData} isAnimationActive={false}>
            {chartData.map((point, index) => (
              <Cell
                key={`${point.category}-${index}`}
                fill={getColorForCategory(point.category, categories)}
              />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
      {/* SmartLegend */}
      <SmartLegend payload={legendItems} layout="horizontal" maxVisibleItems={10} />
    </div>
  );
}
